import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  DataTable,
  DescriptionList,
  Drawer,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  Timeline,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { suppliersApi } from '../suppliers/suppliersApi';
import { purchaseOrdersApi } from '../purchase-orders/purchaseOrdersApi';
import ApprovalChain from '../approvals/ApprovalChain';
import AttachmentsCard from '../attachments/AttachmentsCard';
import CommentComposer from '../comments/CommentComposer';
import { buildCommentTimelineItem, useCommentAnchor } from '../comments/CommentTimeline';
import { useAuth } from '../../auth/AuthContext';
import { requisitionPath, purchaseOrderPath } from '../../lib/slug';
import { requisitionsApi, reqStatusMeta, reqPriorityMeta } from './requisitionsApi';
import './requisitions.css';

const ACTIVITY_META = {
  Created: { tone: 'success', icon: <Icon.Sparkles size={14} /> },
  Updated: { tone: 'default', icon: <Icon.Pencil size={14} /> },
  Submitted: { tone: 'primary', icon: <Icon.ArrowUpCircle size={14} /> },
  Approved: { tone: 'success', icon: <Icon.Check size={14} /> },
  Rejected: { tone: 'danger', icon: <Icon.X size={14} /> },
  Cancelled: { tone: 'warning', icon: <Icon.Ban size={14} /> },
  Converted: { tone: 'brand', icon: <Icon.ShoppingCart size={14} /> },
  Commented: { tone: 'default', icon: <Icon.MessageSquare size={14} /> },
};

export default function RequisitionDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission, canActOnStepRole, properties } = useProperty();
  const { user } = useAuth();
  const canCreate = hasPermission('requisitions.create');
  const canApprove = hasPermission('requisitions.approve');
  const canConvert = hasPermission('purchaseorders.create');

  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const [decision, setDecision] = useState(null); // 'approve' | 'reject'
  const [comment, setComment] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Per-line vendor assignment gathered at "Convert to PO" time (one PO per vendor).
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignments, setAssignments] = useState({}); // lineId -> supplierId
  const [suppliers, setSuppliers] = useState([]);

  const [activity, setActivity] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const anchorId = useCommentAnchor(activity);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReq(await requisitionsApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Requisition not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  const loadActivity = useCallback(async (reqId) => {
    try {
      const res = await requisitionsApi.listActivity(reqId, { pageSize: 100 });
      setActivity(res.items || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [us, locs, sups] = await Promise.all([
        catalogApi.listUnits(),
        catalogApi.listLocations(),
        suppliersApi.list({ pageSize: 500, status: 'Active', sortBy: 'name' }).catch(() => null),
      ]);
      setUnits(us || []);
      setLocations(locs || []);
      setSuppliers(sups?.items || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); loadLookups(); }, [load, loadLookups]);

  // Pull activity once the requisition resolves, and canonicalise the URL to the readable serial.
  useEffect(() => {
    if (!req?.id) return;
    loadActivity(req.id);
    const canonical = requisitionPath(req);
    if (`/requisitions/${routeRef}` !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [req, routeRef, loadActivity, navigate]);

  const uomCode = (id_) => units.find((u) => u.id === id_)?.code;
  const locationName = (id_) => locations.find((l) => l.id === id_)?.name;

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      const updated = await fn();
      setReq(updated);
      toast.success(successMsg);
      loadActivity(updated.id);
    } catch (err) {
      toast.error(t('Action failed'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const submit = () => act(() => requisitionsApi.submit(req.id), t('Submitted for approval'));

  const doConvert = async (lineAssignments) => {
    setBusy(true);
    try {
      const pos = await purchaseOrdersApi.convertFromRequisition(req.id, lineAssignments);
      const list = Array.isArray(pos) ? pos : [];
      setAssignOpen(false);
      toast.success(list.length === 1
        ? t('Converted to {number}', { number: list[0].number })
        : t('Converted to {count} purchase orders', { count: list.length }));
      if (list.length === 1) navigate(purchaseOrderPath(list[0]));
      else navigate('/purchase-orders');
    } catch (err) {
      toast.error(t('Could not convert'), { description: err?.message });
      setBusy(false);
    }
  };

  const convertToPo = () => {
    const lines = req.lines || [];
    const needsAssignment = lines.some((l) => !l.preferredSupplierId);
    if (!needsAssignment) { doConvert(null); return; }
    const initial = {};
    lines.forEach((l) => { initial[l.lineId] = l.preferredSupplierId || ''; });
    setAssignments(initial);
    setAssignOpen(true);
  };

  const confirmAssignedConvert = () => {
    const lineAssignments = Object.entries(assignments)
      .filter(([, supplierId]) => supplierId)
      .map(([lineId, supplierId]) => ({ lineId, supplierId }));
    doConvert(lineAssignments);
  };

  const doCancel = async () => { setCancelOpen(false); await act(() => requisitionsApi.cancel(req.id), t('Requisition cancelled')); };

  const confirmDecision = async () => {
    if (decision === 'reject' && !comment.trim()) { toast.error(t('A reason is required to reject.')); return; }
    const mode = decision;
    setDecision(null);
    if (mode === 'approve') await act(() => requisitionsApi.approve(req.id, comment.trim()), t('Requisition approved'));
    else await act(() => requisitionsApi.reject(req.id, comment.trim()), t('Requisition rejected'));
    setComment('');
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await requisitionsApi.remove(req.id);
      toast.success(t('Requisition deleted'));
      navigate('/requisitions');
    } catch (err) {
      toast.error(t('Could not delete'), { description: err?.message });
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !req) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load requisition")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/requisitions')}>{t('Back to requisitions')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = reqStatusMeta(req.status);
  const priorityMeta = reqPriorityMeta(req.priority);
  const isDraft = req.status === 'Draft';
  const isPending = req.status === 'PendingApproval';
  const isApproved = req.status === 'Approved';
  const canDelete = ['Draft', 'Rejected', 'Cancelled'].includes(req.status);

  const generatedPos = req.generatedPurchaseOrders || [];

  const steps = req.approvalSteps || [];
  const hasChain = steps.length > 0;
  const currentStep = hasChain ? steps.find((s) => s.order === req.currentApprovalStepOrder) : null;
  // Without a chain (legacy in-flight docs), anyone with the approve permission may decide.
  const canActNow = isPending && canApprove && (!currentStep || canActOnStepRole(currentStep.roleId));

  const propertyName = req.propertyId
    ? (properties.find((p) => p.id === req.propertyId)?.name || null)
    : null;

  const overviewItems = [
    { term: t('Property'), description: propertyName || <span className="pp-cell-muted">{t('All properties')}</span> },
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    { term: t('Priority'), description: <Badge tone={priorityMeta.tone}>{t(priorityMeta.label)}</Badge> },
    { term: t('Requested by'), description: req.requestedByName || '—' },
    { term: t('Location'), description: locationName(req.locationId) || '—' },
    { term: t('Needed by'), description: req.neededByDate ? new Date(req.neededByDate).toLocaleDateString() : '—' },
    { term: t('Created'), description: new Date(req.createdAt).toLocaleString() },
    { term: t('Submitted'), description: req.submittedAt ? new Date(req.submittedAt).toLocaleString() : '—' },
    { term: req.status === 'Rejected' ? t('Rejected by') : t('Decided by'), description: req.decidedByName ? `${req.decidedByName}${req.decidedAt ? ` · ${new Date(req.decidedAt).toLocaleString()}` : ''}` : '—' },
  ];

  const attachmentsBase = `/requisitions/${req.id}/attachments`;
  const activityItems = activity.map((a) => {
    if (a.type === 'Commented') {
      return buildCommentTimelineItem({
        activity: a,
        apiBasePath: `/requisitions/${req.id}`,
        attachmentsBasePath: attachmentsBase,
        currentUserId: user?.id,
        editingCommentId,
        setEditingCommentId,
        onChanged: () => loadActivity(req.id),
        highlightId: anchorId,
        t,
      });
    }

    const meta = ACTIVITY_META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    const detail = a.metadata?.reason || a.metadata?.comment;
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: `${a.actorName || t('Someone')} ${a.summary}`,
      meta: new Date(a.createdAt).toLocaleString(),
      description: detail ? `“${detail}”` : undefined,
    };
  });

  const lineColumns = [
    { key: 'description', header: t('Description'), width: 320, render: (l) => (
      <div>
        <div style={{ fontWeight: 600 }}>{l.description}</div>
        {l.sku && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{l.sku}</div>}
      </div>
    ) },
    { key: 'supplier', header: t('Supplier'), width: 180, render: (l) => l.preferredSupplierName || <span className="pp-cell-muted">{t('Unassigned')}</span> },
    { key: 'qty', header: t('Qty'), width: 90, align: 'right', render: (l) => l.quantity },
    { key: 'uom', header: t('Unit'), width: 90, render: (l) => uomCode(l.uomId) || <span className="pp-cell-muted">—</span> },
    { key: 'unit', header: t('Est. unit price'), width: 140, align: 'right', render: (l) => money(l.estimatedUnitPrice) },
    { key: 'total', header: t('Est. total'), width: 140, align: 'right', render: (l) => money(l.estimatedTotal) },
  ];

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/requisitions')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Requisitions')}
      </Button>

      <PageHeader
        title={req.title}
        description={
          <span>
            <span className="req-detail__number">{req.number}</span>
            {propertyName && <> · {propertyName}</>}
          </span>
        }
        icon={<Icon.ListChecks size={22} />}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button variant="ghost" leadingIcon={<Icon.Printer size={15} />} onClick={() => navigate(requisitionPath(req, '/print'))}>{t('Print')}</Button>
            {isDraft && canCreate && (
              <>
                <Button variant="secondary" leadingIcon={<Icon.Pencil size={15} />} onClick={() => navigate(requisitionPath(req, '/edit'))}>{t('Edit')}</Button>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={submit}>{t('Submit')}</Button>
              </>
            )}
            {isPending && canCreate && (
              <Button
                variant="secondary"
                leadingIcon={<Icon.Pencil size={15} />}
                title={t('Editing withdraws the requisition from approval — it returns to Draft and must be resubmitted.')}
                onClick={() => navigate(requisitionPath(req, '/edit'))}
              >
                {t('Edit')}
              </Button>
            )}
            {isPending && canApprove && canActNow && (
              <>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.Check size={15} />} onClick={() => { setComment(''); setDecision('approve'); }}>{t('Approve')}</Button>
                <Button variant="secondary" loading={busy} leadingIcon={<Icon.X size={15} />} onClick={() => { setComment(''); setDecision('reject'); }}>{t('Reject')}</Button>
              </>
            )}
            {generatedPos.length === 1 && (
              <Button variant="primary" leadingIcon={<Icon.ShoppingCart size={15} />} onClick={() => navigate(purchaseOrderPath({ number: generatedPos[0].number, id: generatedPos[0].purchaseOrderId }))}>
                {t('View {number}', { number: generatedPos[0].number })}
              </Button>
            )}
            {/* Fallback: a requisition is fully approved but the automatic PO generation didn't run. */}
            {isApproved && canConvert && (
              <Button variant="primary" loading={busy} leadingIcon={<Icon.ShoppingCart size={15} />} onClick={convertToPo}>{t('Convert to PO')}</Button>
            )}
            {(isDraft || isPending) && canCreate && (
              <Button variant="ghost" loading={busy} onClick={() => setCancelOpen(true)}>{t('Cancel')}</Button>
            )}
            {canDelete && canCreate && (
              <IconButton aria-label={t('Delete requisition')} variant="ghost" onClick={() => setDeleteOpen(true)}><Icon.Trash size={16} /></IconButton>
            )}
          </div>
        }
      />

      {isPending && currentStep && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone={canActNow ? 'info' : 'warning'}>
            {t('Step {step} of {total}: awaiting {role} approval.', { step: currentStep.order, total: steps.length, role: currentStep.roleName || t('approver') })}
            {canActNow ? ` ${t('You can approve or reject this step.')}` : ` ${t('You do not hold the role required for this step.')}`}
          </Banner>
        </div>
      )}
      {req.status === 'Rejected' && req.decisionComment && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="danger">{t('Rejected: {comment}', { comment: req.decisionComment })}</Banner></div>
      )}
      {req.status === 'Approved' && req.decisionComment && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="success">{t('Approved: {comment}', { comment: req.decisionComment })}</Banner></div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Estimated total')} value={money(req.estimatedTotal)} />
        <KpiTile label={t('Lines')} value={req.lines?.length || 0} />
        <KpiTile label={t('Priority')} value={t(priorityMeta.label)} />
        <KpiTile label={t('Status')} value={t(statusMeta.label)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {req.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{req.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header><Card.Title>{t('Lines')}</Card.Title></Card.Header>
          <Card.Body>
            <DataTable columns={lineColumns} data={req.lines || []} rowKey={(l) => l.lineId} density="comfortable" />
          </Card.Body>
        </Card>

        {generatedPos.length > 0 && (
          <Card>
            <Card.Header><Card.Title>{t('Purchase orders')}</Card.Title></Card.Header>
            <Card.Body>
              <div className="req-po-links">
                {generatedPos.map((po) => (
                  <button
                    key={po.purchaseOrderId}
                    type="button"
                    className="req-po-link"
                    onClick={() => navigate(purchaseOrderPath({ number: po.number, id: po.purchaseOrderId }))}
                  >
                    <Icon.ShoppingCart size={16} />
                    <span className="req-po-link__number">{po.number}</span>
                    <span className="req-po-link__supplier">{po.supplierName || t('Unassigned supplier')}</span>
                    <Icon.ChevronRight size={16} />
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {hasChain && (
          <Card>
            <Card.Header><Card.Title>{t('Approval chain')}</Card.Title></Card.Header>
            <Card.Body>
              <ApprovalChain
                steps={steps}
                currentStepOrder={req.currentApprovalStepOrder}
                currency={req.currency}
                showSignatures
              />
            </Card.Body>
          </Card>
        )}

        {(req.submittedAt || (!hasChain && req.decidedByName)) && (
          <Card>
            <Card.Header><Card.Title>{t('Authorisation')}</Card.Title></Card.Header>
            <Card.Body>
              <div className="req-sign__grid">
                {req.submittedAt && (
                  <div className="req-sign__block">
                    <span className="req-sign__role">{t('Requested by')}</span>
                    <div className="req-sign__mark">
                      {req.submittedSignature
                        ? <img src={req.submittedSignature} alt={t('Requester signature')} />
                        : <span className="req-sign__unsigned">{t('— not signed —')}</span>}
                    </div>
                    <span className="req-sign__name">{req.requestedByName || '—'}</span>
                    <span className="req-sign__meta">{new Date(req.submittedAt).toLocaleString()}</span>
                  </div>
                )}
                {!hasChain && req.decidedByName && (
                  <div className="req-sign__block">
                    <span className="req-sign__role">{req.status === 'Rejected' ? t('Rejected by') : t('Approved by')}</span>
                    <div className="req-sign__mark">
                      {req.decisionSignature
                        ? <img src={req.decisionSignature} alt={t('Approver signature')} />
                        : <span className="req-sign__unsigned">{t('— not signed —')}</span>}
                    </div>
                    <span className="req-sign__name">{req.decidedByName}</span>
                    <span className="req-sign__meta">{req.decidedAt ? new Date(req.decidedAt).toLocaleString() : ''}</span>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        <AttachmentsCard basePath={`/requisitions/${req.id}/attachments`} canManage={canCreate} />

        <Card>
          <Card.Header><Card.Title>{t('Activity')}</Card.Title></Card.Header>
          <Card.Body>
            <CommentComposer
              basePath={`/requisitions/${req.id}`}
              onPosted={() => loadActivity(req.id)}
            />
            {activityItems.length === 0 ? (
              <EmptyState icon={<Icon.Activity size={26} />} title={t('No activity yet')} description={t('Lifecycle events and comments will appear here.')} />
            ) : (
              <Timeline items={activityItems} className="req-activity__timeline" />
            )}
          </Card.Body>
        </Card>
      </div>

      <Drawer
        open={assignOpen}
        onClose={() => (busy ? null : setAssignOpen(false))}
        title={t('Assign vendors')}
        description={t('Pick a vendor for each line. One purchase order is created per vendor.')}
        size="md"
      >
        {(() => {
          const lines = req.lines || [];
          const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.name }));
          const assignedIds = new Set(Object.values(assignments).filter(Boolean));
          const unassignedCount = lines.filter((l) => !assignments[l.lineId]).length;
          const poCount = assignedIds.size + (unassignedCount > 0 ? 1 : 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className="ps-field">
                <span className="ps-field__label">{t('Assign all lines to')}</span>
                <Combobox
                  options={supplierOptions}
                  value=""
                  onChange={(v) => {
                    if (!v) return;
                    const next = {};
                    lines.forEach((l) => { next[l.lineId] = v; });
                    setAssignments(next);
                  }}
                  placeholder={t('Search suppliers…')}
                  searchPlaceholder={t('Search suppliers…')}
                  size="sm"
                  emptyText={t('No matches')}
                  ariaLabel={t('Assign all lines to')}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {lines.map((l) => (
                  <div key={l.lineId} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px', gap: 'var(--space-3)', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</div>
                      <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>
                        {l.quantity} {uomCode(l.uomId) || ''}
                      </div>
                    </div>
                    <Combobox
                      options={supplierOptions}
                      value={assignments[l.lineId] || ''}
                      onChange={(v) => setAssignments((prev) => ({ ...prev, [l.lineId]: v }))}
                      placeholder={t('Select vendor…')}
                      searchPlaceholder={t('Search suppliers…')}
                      size="sm"
                      emptyText={t('No matches')}
                      ariaLabel={t('Vendor for {line}', { line: l.description })}
                    />
                  </div>
                ))}
              </div>
              {unassignedCount > 0 && (
                <Banner tone="warning">
                  {t('{count} line(s) have no vendor and will be grouped into one purchase order without a supplier.', { count: unassignedCount })}
                </Banner>
              )}
              <div className="req-form__actions">
                <Button variant="ghost" onClick={() => setAssignOpen(false)} disabled={busy}>{t('Cancel')}</Button>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.ShoppingCart size={15} />} onClick={confirmAssignedConvert}>
                  {t('Create {count} purchase order(s)', { count: poCount })}
                </Button>
              </div>
            </div>
          );
        })()}
      </Drawer>

      <Drawer
        open={!!decision}
        onClose={() => (busy ? null : setDecision(null))}
        title={decision === 'approve' ? t('Approve requisition') : t('Reject requisition')}
        description={decision === 'approve' ? t('Optionally add a note for the requester.') : t('Tell the requester why this is being rejected.')}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Textarea
            label={decision === 'approve' ? t('Comment (optional)') : t('Reason')}
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autoFocus
          />
          <div className="req-form__actions">
            <Button variant="ghost" onClick={() => setDecision(null)} disabled={busy}>{t('Cancel')}</Button>
            <Button variant={decision === 'approve' ? 'primary' : 'secondary'} loading={busy} onClick={confirmDecision}>
              {decision === 'approve' ? t('Approve') : t('Reject')}
            </Button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={cancelOpen}
        title={t('Cancel requisition')}
        description={t("Cancelling stops this requisition from being approved or ordered. This can't be undone.")}
        confirmLabel={t('Cancel requisition')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete requisition')}
        description={t("Permanently delete {number}? This can't be undone.", { number: req.number })}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={busy}
        onConfirm={doDelete}
        onCancel={() => (busy ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
